// src/pages/Base64Page.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import KakaoAdFit from "../components/KakaoAdFit"; 
import "./base64Page.css";

function Base64Page() {
  const [darkMode, setDarkMode] = useState(false);

  // CSR 기준 canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Base64 인코더/디코더",
      description:
        "텍스트를 Base64로 인코딩하거나 Base64를 UTF-8 텍스트로 디코딩하는 TooLoves 도구",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "Base64, Base64 Encoder,Base64 Decoder,xBase64인코더,Base64디코더, 베이스64, 인코딩, 디코딩, 텍스트 변환, URL-safe, TooLoves, 무료",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "KRW",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // 다크/라이트 모드에 따라 meta theme-color 동기화
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  // 숨겨진 파일 입력 참조
  const fileInputRef = useRef(null);

  // 입력 참조
  const inputRef = useRef(null);

  // 상태
  const [mode, setMode] = useState("encode"); // encode | decode
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [decodedBytes, setDecodedBytes] = useState(null); // decode 결과 보관
  const [urlSafe, setUrlSafe] = useState(false);
  const [error, setError] = useState("");

  // 파일 버튼 클릭 -> 숨겨진 파일 input 클릭
  const onPickClick = () => {
    if (!fileInputRef.current) return;
    try {
      fileInputRef.current.value = "";
    } catch {}
    fileInputRef.current.click();
  };

  // 파일 선택 시 텍스트 로딩
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setInputText(text);
    } catch (err) {
      setError("파일을 읽는 중 오류가 발생했습니다.");
    }
  };

  // textarea 자동 높이 조정
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autoResize(inputRef.current);
  }, [inputText]);

  // Base64 유틸
  const bytesToBase64 = (bytes) => {
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      const sub = bytes.subarray(i, i + chunk);
      binary += String.fromCharCode(...sub);
    }
    return btoa(binary);
  };

  const base64ToBytes = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  const encodeUtf8ToBase64 = (text, { urlSafe }) => {
    const bytes = new TextEncoder().encode(text);
    let b64 = bytesToBase64(bytes);
    if (urlSafe) {
      b64 = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    }
    return b64;
  };

  const normalizeBase64Input = (raw, { urlSafe }) => {
    let s = raw || "";
    // 공백 제거
    s = s.replace(/\s+/g, "");
    if (urlSafe) s = s.replace(/-/g, "+").replace(/_/g, "/");
    // padding
    const pad = s.length % 4;
    if (pad > 0) s = s + "=".repeat(4 - pad);
    return s;
  };

  const decodeBase64ToUtf8 = (raw, { urlSafe }) => {
    const normalized = normalizeBase64Input(raw, { urlSafe });
    const bytes = base64ToBytes(normalized);
    const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return { text, bytes };
  };

  // 가독성 포맷터: 76자 줄바꿈 + 들여쓰기(인코딩 모드 전용)
  const wrapAndIndent = (str, width = 76, indent = "  ") => {
    if (!str) return "";
    const out = [];
    for (let i = 0; i < str.length; i += width) {
      out.push(indent + str.slice(i, i + width));
    }
    return out.join("\n");
  };

  const getDisplayOutput = () => {
    if (!outputText) return "";
    // Base64 인코딩 결과만 76자 줄바꿈+들여쓰기 적용 (디코딩 텍스트는 원본 유지)
    if (mode === "encode") {
      return wrapAndIndent(outputText, 76, "  ");
    }
    return outputText;
  };

  // 변환 처리
  const transform = () => {
    setError("");
    try {
      if (mode === "encode") {
        const b64 = encodeUtf8ToBase64(inputText, { urlSafe });
        setOutputText(b64);
        setDecodedBytes(null);
      } else {
        const { text, bytes } = decodeBase64ToUtf8(inputText, { urlSafe });
        setOutputText(text);
        setDecodedBytes(bytes);
      }
    } catch (err) {
      setOutputText("");
      setDecodedBytes(null);
      setError("유효하지 않은 입력이거나 변환 중 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    transform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText, mode, urlSafe]);

  // 클립보드 복사 (표시용 포맷 기준)
  const handleCopy = async () => {
    try {
      const data = getDisplayOutput() || "";
      await navigator.clipboard.writeText(data);
    } catch (err) {
      setError("클립보드 복사에 실패했습니다.");
    }
  };

  // 다운로드: 두 모드 모두 TXT로 저장 (표시용 포맷 기준)
  const handleDownload = () => {
    try {
      const data = getDisplayOutput() || "";
      const blob = new Blob([data], { type: "text/plain;charset=utf-8" });
      const filename = mode === "encode" ? "base64.txt" : "decoded.txt";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("다운로드 중 오류가 발생했습니다.");
    }
  };

  const handleReset = () => {
    setInputText("");
    setOutputText("");
    setDecodedBytes(null);
    setMode("encode");
    setUrlSafe(false);
    setError("");
    requestAnimationFrame(() => {
      autoResize(inputRef.current);
    });
  };

  const handleSwap = () => {
    if (!outputText) return;
    if (mode === "encode") {
      setInputText(outputText);
      setMode("decode");
    } else {
      setInputText(outputText);
      setMode("encode");
    }
  };

  const isOutputEmpty = !outputText || outputText.trim() === "";
  const displayOutput = getDisplayOutput();

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "ko" }}>
        <title>Base64 인코더/디코더 | TooLoves</title>
        <meta
          name="description"
          content="텍스트를 Base64로 인코딩하거나 Base64 문자열을 UTF-8 텍스트로 디코딩하는 온라인 도구"
        />
        <meta
          name="keywords"
          content="Base64, 인코딩, 디코딩, URL-safe, 텍스트 변환, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 초기 theme-color, 런타임에서 useEffect로 동기화 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="Base64 인코더/디코더 | TooLoves" />
        <meta
          property="og:description"
          content="텍스트/base64 간 변환을 손쉽게 수행"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-base64.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Base64 인코더/디코더 | TooLoves" />
        <meta
          name="twitter:description"
          content="텍스트/base64 간 변환을 손쉽게 수행"
        />
        <meta name="twitter:image" content="/og-base64.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* 상단 앱바 */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/" className="brand-link" aria-label="TooLoves 홈으로 이동">
            TooLoves
          </Link>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="mode-toggle" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? "라이트 모드" : "다크 모드"}
            </button>
          </div>
        </div>
      </header>

      {/* 본문 */}
      <main
        className="container"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        {/* ✅ 기존 광고 위치: 제거됨 */}
        <section className="generator-card">
          <h2 className="card-title">Base64 인코더/디코더</h2>
          <p className="card-subtitle">
            텍스트를 Base64로 인코딩하거나 Base64를 UTF-8 텍스트로 디코딩할 수 있습니다.
          </p>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* 파일 업로드 (숨김 input + 버튼) */}
            <div className="input-row">
              <input
                ref={fileInputRef}
                id="b64-file-input"
                type="file"
                accept="text/plain,.txt"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={onPickClick}
                aria-label="텍스트 파일 선택"
                className="btn btn-primary"
              >
                파일 선택
              </button>
            </div>

            {/* 입력 */}
            <div className="input-row" style={{ marginTop: 8 }}>
              <div className="io-wrap" style={{ width: "100%" }}>
                <textarea
                  ref={inputRef}
                  className="text-input text-inputtextarea mono"
                  placeholder={
                    mode === "encode"
                      ? "여기에 텍스트를 입력하거나 파일을 선택하세요."
                      : "여기에 Base64 문자열을 붙여넣으세요."
                  }
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    display: "block",
                    maxWidth: "1200px",
                    margin: "0 auto",
                  }}
                  spellCheck={false}
                />
              </div>
            </div>

            {/* 옵션 */}
            <div className="options-row" style={{ gap: 16 }}>
              <div className="field">
                <label className="form-label">모드</label>
                <select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="encode">인코딩 (텍스트 → Base64)</option>
                  <option value="decode">디코딩 (Base64 → 텍스트)</option>
                </select>
              </div>

              <div className="field switch">
                <label className="form-label">URL-safe</label>
                <input
                  type="checkbox"
                  checked={urlSafe}
                  onChange={(e) => setUrlSafe(e.target.checked)}
                />
              </div>
            </div>
          </form>

          {/* 출력 */}
          <div className="metadata-section" style={{ paddingTop: 8 }}>
            <div className="block">
              <div className="label-row">
                <label htmlFor="b64-output" className="label-strong">
                  출력 결과
                </label>
              </div>

              <div
                id="b64-output"
                role="textbox"
                aria-readonly="true"
                tabIndex={0}
                className="mono-box"
                style={{ whiteSpace: "pre-wrap" }} // 줄바꿈/들여쓰기 보존
              >
                {displayOutput ||
                  (mode === "encode"
                    ? "인코딩 결과가 여기에 표시됩니다."
                    : "디코딩 결과가 여기에 표시됩니다.")}
              </div>

              {error && (
                <p className="hint" role="alert" style={{ color: "#ef4444", marginTop: 8 }}>
                  {error}
                </p>
              )}
            </div>

            {/* 하단 동작: 복사/다운로드 + 스왑/초기화 */}
            <div className="actions">
              <button
                className="btn-primary"
                onClick={handleCopy}
                type="button"
                aria-label="출력 결과 복사"
                disabled={isOutputEmpty}
              >
                복사
              </button>
              <button
                className="btn-primary"
                onClick={handleDownload}
                type="button"
                aria-label="출력 결과 txt 다운로드"
                disabled={isOutputEmpty}
              >
                txt 다운로드
              </button>
              <button className="btn-secondary" onClick={handleSwap} type="button">
                스왑
              </button>
              <button className="btn-secondary" onClick={handleReset} type="button">
                초기화
              </button>
            </div>

            {/* 디코딩 시 바이트 길이 힌트 */}
            {mode === "decode" && decodedBytes && (
              <p className="hint" style={{ marginTop: 8 }}>
                바이트 길이: {decodedBytes.byteLength.toLocaleString()} bytes
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="법적 고지 링크">
          <Link to="/terms" className="footer-link">
            이용약관
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/privacy" className="footer-link">
            개인정보처리방침
          </Link>
        </nav>
      </footer>

      {/* ✅ 광고: 푸터 밑으로 이동 */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
        <KakaoAdFit />
      </div>
    </div>
  );
}

export default Base64Page;